import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  LabelList,
  type LabelProps,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { paletteFor } from '@/components/readiness/recommendation-palette';
import type { Thresholds } from '@/components/ui/charts/shared';
import { cn } from '@/lib/utils/cn';
import type { ReadinessScorecard } from '@/lib/api/readiness';

/**
 * Scorecard summary tile for the engagement overview (C20-132 rewrite of
 * T-0097's radar tile).
 *
 * Prototype contract (`screens-engagement.js → window.Screens.overview`,
 * the "Go-Live Readiness · weighted scorecard" card): a bar chart across
 * the rubric categories with a posture-coloured fill (healthy/at-risk/
 * critical) and the weighted total + recommendation chip in the card
 * header.
 *
 * One unit everywhere (C2-71): the tile prints the weighted total as the
 * rounded percentage Health & Readiness' gauge prints (`readiness-gauge.tsx`),
 * never "0.44 / 5". The per-category bars are the API's `aggregate_percent`
 * on a 0..100 axis, and their colour bands are the engagement's own Go /
 * Conditional thresholds that the scorecard DTO carries:
 *   >= go           → posture-healthy
 *   >= conditional  → posture-at-risk
 *   else            → posture-critical
 *
 * Nothing is computed here beyond rounding for display: the total, the
 * category percentages, the recommendation and the thresholds are the
 * API's (R6).
 *
 * The "Open criteria" CTA opens Health & Readiness on its first criterion,
 * where the old Go-Live scorecard redirects too (T-0224).
 */
export interface ScorecardSummaryProps {
  engagementId: string;
  scorecard: ReadinessScorecard;
}

interface BarDatum {
  /** Truncated label rendered on the X axis (prototype slices to 8 chars). */
  label: string;
  /** Full category label retained for the tooltip / aria-label. */
  fullLabel: string;
  /** The category's weighted percentage, 0..100. */
  percent: number;
}

/** The posture colour a readiness percentage falls in under the engagement's gates. */
export function scorecardBandColor(percent: number, thresholds: Thresholds): string {
  if (percent >= thresholds.go) return 'var(--posture-healthy)';
  if (percent >= thresholds.conditional) return 'var(--posture-at-risk)';
  return 'var(--posture-critical)';
}

/** The percentage as the gauge prints it: a whole number with the sign. */
export function formatReadinessPercent(percent: number | null): string {
  return percent === null ? '—' : `${Math.round(percent)}%`;
}

export function ScorecardSummary({ engagementId, scorecard }: ScorecardSummaryProps) {
  const palette = paletteFor(scorecard.recommendation);
  const thresholds = scorecard.thresholds;

  const bars: BarDatum[] = scorecard.categories.map((c) => ({
    label: c.category.length > 8 ? `${c.category.slice(0, 8)}…` : c.category,
    fullLabel: c.category,
    percent: Math.max(0, Math.min(100, c.aggregate_percent ?? 0)),
  }));

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="scorecard-summary-title"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id="scorecard-summary-title" className="t-h3">
            Go-Live Readiness · weighted scorecard
          </h2>
          <p className="t-meta">
            Weighted total{' '}
            <strong className="font-semibold text-ink-900" data-testid="scorecard-total">
              {formatReadinessPercent(scorecard.total_percent)}
            </strong>{' '}
            · Recommendation:{' '}
            <span
              className={cn(
                'inline-flex h-[22px] items-center rounded-full px-2.5 text-meta font-medium',
                palette.badgeClass,
              )}
            >
              {palette.label}
            </span>
          </p>
          <p className="t-meta text-ink-500">
            Go at {Math.round(thresholds.go)}%, Conditional at {Math.round(thresholds.conditional)}%
          </p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/engagements/$engagementId/health-readiness/criteria" params={{ engagementId }}>
            Open criteria
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {bars.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No criteria scored yet. Score them on Health & Readiness to fill the roll-up.
        </p>
      ) : (
        <div
          role="img"
          aria-label={`Readiness bar chart across ${bars.length} categories: ${bars
            .map((b) => `${b.fullLabel} ${formatReadinessPercent(b.percent)}`)
            .join(', ')}`}
          className="h-[160px] w-full"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} margin={{ top: 20, right: 8, bottom: 8, left: 8 }} barSize={36}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                interval={0}
              />
              <YAxis hide domain={[0, 100]} />
              <Bar dataKey="percent" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                {bars.map((b, i) => (
                  <Cell
                    key={`${b.fullLabel}-${i}`}
                    fill={scorecardBandColor(b.percent, thresholds)}
                  />
                ))}
                <LabelList dataKey="percent" position="top" content={renderBarLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

/**
 * Inline above-bar label — mirrors the prototype's per-bar score caption
 * (`<text y="${y - 4}" ...>${d.value}</text>`), as a whole percentage.
 */
function renderBarLabel(props: LabelProps) {
  const { x, y, width, value } = props;
  if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number') {
    return null;
  }
  const percent = typeof value === 'number' ? value : Number(value ?? 0);
  return (
    <text
      x={x + width / 2}
      y={y - 4}
      textAnchor="middle"
      fontSize={11}
      fill="var(--ink-700)"
      fontWeight={500}
    >
      {formatReadinessPercent(percent)}
    </text>
  );
}
